// src/components/ProgressModal.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { Dialog } from "@headlessui/react";
import Lottie from "lottie-react";

// ✔ Your existing animation – works fine
import uploadingAnim from "../assets/uploading.json";

export default function ProgressModal({
  open,
  onClose,
  onDoneClose,
  headline = "Processing…",
  subline,
  percent = 0,
  logs = [],
  done = false
}) {
  const lottieRef = useRef(null);

  useEffect(() => {
    if (lottieRef.current && !done) {
      lottieRef.current.setSpeed(1);
    }
  }, [done]);

  const progressLabel = useMemo(() => `${Math.floor(percent)}%`, [percent]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">

          {/* ---------------- Animation + Title ---------------- */}
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 shrink-0">

              {/* ✔ Uploading animation */}
              {!done ? (
                <Lottie lottieRef={lottieRef} animationData={uploadingAnim} loop />
              ) : (
                /* ✔ Success state WITHOUT needing success_tick.json */
                <div className="flex items-center justify-center w-full h-full">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-green-600"
                      fill="none"
                      strokeWidth="3"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              )}

            </div>

            <div className="flex-1">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {done ? "Completed Successfully" : headline}
              </Dialog.Title>

              {subline && (
                <p className="text-sm text-gray-600 mt-0.5">
                  {done ? "All applications processed successfully." : subline}
                </p>
              )}

              {/* Progress Bar */}
              {!done && (
                <div className="mt-3">
                  <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-3 bg-blue-600 transition-all"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="text-right text-xs text-gray-600 mt-1">
                    {progressLabel}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ---------------- Logs ---------------- */}
          <div className="mt-4 h-52 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
            {logs.length === 0 ? (
              <div className="text-gray-500 italic">Waiting for updates…</div>
            ) : (
              logs.map((line, idx) => (
                <div key={idx} className="whitespace-pre-wrap text-gray-800">
                  {line}
                </div>
              ))
            )}
          </div>

        {/* ---------------- Close Button ---------------- */}
        <div className="mt-4 flex justify-end">
        <button
            onClick={() => {
            if (done && onDoneClose) onDoneClose();  // ✅ redirect when completed
            onClose();                               // ✅ always close modal
            }}
            disabled={!done}
            className={`px-4 py-2 text-sm rounded-md border ${
            done
                ? "border-green-600 bg-green-600 text-white hover:bg-green-700"
                : "border-gray-300 text-gray-500"
            }`}
        >
            {done ? "Close" : "Running…"}
        </button>
        </div>

        </Dialog.Panel>
      </div>
    </Dialog>
  );
}